import { useEffect } from 'react';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import prose from '@/styles/prose.module.css';
import LegalDoc, { LegalSection as Section } from '@/components/legal/LegalDoc';

const Paragraph = ({ children }) => (
  <p className={prose.paragraph}>{children}</p>
);

const BulletList = ({ items }) => (
  <ul className={prose.list}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  usePageMeta(PAGE_META.privacy);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="privacy-policy-page">
      <LandingHeader />

      <main>

      <LegalDoc
        title="Privacy Policy"
        updated="August 13, 2026"
        isoDate="2026-08-13"
        readingTime="2 min read"
        testId="privacy-document"
      >

        <Section title="1. Introduction">
          <Paragraph>
            My Case Loan, LLC (&quot;MyCaseLoan.com,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is a Florida-based limited liability company committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our pre-settlement funding platform (&quot;Service&quot;).
          </Paragraph>
        </Section>

        <Section title="2. Information We Collect">
          <h3 className={prose.subhead}>2.1 Personal Information</h3>
          <Paragraph>We may collect the following personal information:</Paragraph>
          <BulletList items={[
            'Name and contact information (email, phone number, address)',
            'Account credentials',
            'Professional information (for lawyers and funders)',
            'Case-related information (for clients/plaintiffs)',
            'Financial information related to funding requests',
            'Documents uploaded to the platform',
            'Information you enter into an application you begin but do not complete',
          ]} />

          <h3 className={prose.subhead}>2.2 Automatically Collected Information</h3>
          <Paragraph>When you access our Service, we automatically collect:</Paragraph>
          <BulletList items={[
            'Device information (type, operating system, browser)',
            'IP address and location data',
            'Usage data (pages visited, time spent, actions taken)',
            'Cookies and similar tracking technologies',
            'Information stored in your own browser to preserve your progress in a form, so you can return and finish it',
          ]} />
        </Section>

        <Section title="3. How We Use Your Information">
          <Paragraph>We use collected information to:</Paragraph>
          <BulletList items={[
            'Provide, maintain, and improve our Service',
            'Process funding applications and facilitate transactions',
            'Communicate with you about your account and applications',
            'Contact you about an application you started but did not complete, so you can finish it',
            'Verify user identities and prevent fraud',
            'Comply with legal obligations',
            'Send administrative information and updates',
            'Respond to inquiries and provide customer support',
            'Analyze usage patterns to improve user experience',
          ]} />
        </Section>

        <Section title="4. Information Sharing">
          <Paragraph>We may share your information with:</Paragraph>
          <BulletList items={[
            <><strong>Other Platform Users:</strong> Lawyers, funders, and clients as necessary for the funding process (with appropriate access controls)</>,
            <><strong>Service Providers:</strong> Third parties who assist in operating our Service</>,
            <><strong>Legal Requirements:</strong> When required by law or to protect our rights</>,
            <><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</>,
          ]} />
          <Paragraph>We do not sell your personal information to third parties for marketing purposes.</Paragraph>
        </Section>

        <Section title="5. Data Security">
          <Paragraph>We implement appropriate technical and organizational security measures to protect your information, including:</Paragraph>
          <BulletList items={[
            'Encryption of data in transit and at rest',
            'Secure authentication mechanisms',
            'Regular security assessments',
            'Access controls and audit logging',
            'Employee training on data protection',
          ]} />
        </Section>

        <Section title="6. Data Retention">
          <Paragraph>
            We retain your personal information for as long as necessary to provide our Service and fulfill the purposes outlined in this policy. We may retain certain information for longer periods as required by law or for legitimate business purposes (such as resolving disputes or enforcing agreements).
          </Paragraph>
          <Paragraph>
            Information from an application you began but did not complete is kept so that we can help you finish it, and you may ask us to delete it at any time using the contact details below. Progress saved in your own browser is stored only on your device, expires after seven days, and is cleared when you submit your application.
          </Paragraph>
        </Section>

        <Section title="7. Your Rights">
          <Paragraph>Depending on your location, you may have the right to:</Paragraph>
          <BulletList items={[
            'Access your personal information',
            'Correct inaccurate information',
            'Delete your personal information',
            'Object to or restrict processing',
            'Data portability',
            'Withdraw consent',
          ]} />
          <Paragraph>To exercise these rights, please contact us using the information provided below.</Paragraph>
        </Section>

        <Section title="8. Cookies">
          <Paragraph>
            We use cookies and similar technologies to enhance your experience on our Service. You can control cookie preferences through your browser settings. Note that disabling cookies may affect the functionality of certain features.
          </Paragraph>
        </Section>

        <Section title="9. Third-Party Links">
          <Paragraph>
            Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
          </Paragraph>
        </Section>

        <Section title="10. Children's Privacy">
          <Paragraph>
            Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it.
          </Paragraph>
        </Section>

        <Section title="11. Changes to This Policy">
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the &quot;Last updated&quot; date. We encourage you to review this policy periodically.
          </Paragraph>
        </Section>

        <Section title="12. Contact Us">
          <Paragraph>If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:</Paragraph>
          <p className={prose.address}>
            My Case Loan, LLC<br />
            A Florida Limited Liability Company<br />
            Email: privacy@mycaseloan.com
          </p>
        </Section>

      </LegalDoc>
      </main>


      <LandingFooter />
    </div>
  );
}
